import { useCallback, useState } from 'react';
import classNames from 'classnames';
import {
  Box,
  Header,
  IconButton,
  Line,
  MenuItem,
  Scroll,
  Text,
  Tooltip,
  TooltipProvider,
  as,
  config,
} from 'folds';
import { Avatar, AvatarShape } from '$components/avatar';
import type { MatrixEvent, Room, RoomMember, Relations } from '$types/matrix-sdk';
import { getAvatarUrl, getMemberDisplayName } from '$utils/room/display';
import { eventWithShortcode, getMxIdLocalPart } from '$utils/matrix';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { useRelations } from '$hooks/useRelations';
import { getHexcodeForEmoji, getShortcodeFor } from '$plugins/emoji';
import { useAtomValue } from 'jotai';
import { nicknamesAtom } from '$state/nicknames';
import { UserAvatar } from '$components/user-avatar';
import { composerIcon, userFallbackIcon, X } from '$components/icons/phosphor';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { useOpenUserRoomProfile } from '$state/hooks/userRoomProfile';
import { useSpaceOptionally } from '$hooks/useSpace';
import { getMouseEventCords } from '$utils/dom';
import { ReactionViewerMenuItem } from './ReactionViewerMenuItem';
import * as css from './ReactionViewer.css';

type ReactionViewerProps = {
  room: Room;
  initialKey?: string;
  relations: Relations;
  requestClose: () => void;
};
export const ReactionViewer = as<'div', ReactionViewerProps>(
  ({ className, room, initialKey, relations, requestClose, ...props }, ref) => {
    const mx = useMatrixClient();
    const useAuthentication = useMediaAuthentication();
    const reactions = useRelations(
      relations,
      useCallback((rel) => [...(rel.getSortedAnnotationsByKey() ?? [])], [])
    );
    const space = useSpaceOptionally();
    const openProfile = useOpenUserRoomProfile();
    const nicknames = useAtomValue(nicknamesAtom);

    const [selectedKey, setSelectedKey] = useState<string>(() => {
      if (initialKey) return initialKey;
      const defaultReaction = reactions.find((reaction) => typeof reaction[0] === 'string');
      return defaultReaction ? defaultReaction[0] : '';
    });

    const getName = (member: RoomMember) =>
      getMemberDisplayName(room, member.userId, nicknames) ??
      getMxIdLocalPart(member.userId) ??
      member.userId;

    const getReactionsForKey = (key: string): MatrixEvent[] => {
      const reactSet = reactions.find(([k]) => k === key)?.[1];
      if (!reactSet) return [];
      return Array.from(reactSet);
    };

    const selectedReactions = getReactionsForKey(selectedKey);

    const getShortcodeForKey = (key: string, evts: Set<MatrixEvent>) => {
      const events = Array.from(evts);
      return (
        events.find(eventWithShortcode)?.getContent().shortcode ??
        getShortcodeFor(getHexcodeForEmoji(key)) ??
        key
      );
    };

    return (
      <Box
        className={classNames(css.ReactionViewer, className)}
        direction="Column"
        {...props}
        ref={ref}
      >
        <Header className={css.Header} variant="Surface" size="400">
          <Box grow="Yes">
            <Text size="H3">Reaction</Text>
          </Box>
          <IconButton size="300" onClick={requestClose}>
            {composerIcon(X)}
          </IconButton>
        </Header>
        <Line variant="Surface" direction="Horizontal" size="300" />
        <Box className={css.Body} direction="Row" grow="Yes">
          <Box shrink="No" className={css.Sidebar}>
            <div className={css.SidebarContent}>
              {reactions.map(([key, evts]) => {
                if (typeof key !== 'string') return null;
                const shortcode = getShortcodeForKey(key, evts);
                return (
                  <TooltipProvider
                    key={key}
                    position="Left"
                    tooltip={
                      <Tooltip>
                        <Text size="T300">{`:${shortcode}:`}</Text>
                      </Tooltip>
                    }
                  >
                    {(targetRef) => (
                      <ReactionViewerMenuItem
                        ref={targetRef}
                        mx={mx}
                        reaction={key}
                        count={evts.size}
                        aria-selected={key === selectedKey}
                        onClick={() => setSelectedKey(key)}
                        useAuthentication={useAuthentication}
                      />
                    )}
                  </TooltipProvider>
                );
              })}
            </div>
          </Box>
          <Line variant="Surface" direction="Vertical" size="300" />
          <Box grow="Yes">
            <Scroll visibility="Hover" hideTrack size="300">
              <Box className={css.Content} direction="Column">
                {selectedReactions.map((mEvent) => {
                  const senderId = mEvent.getSender();
                  if (!senderId) return null;
                  const member = room.getMember(senderId);
                  const name = (member ? getName(member) : getMxIdLocalPart(senderId)) ?? senderId;

                  const avatarMxcUrl = member?.getMxcAvatarUrl();
                  const avatarUrl = getAvatarUrl(mx, avatarMxcUrl, 100, useAuthentication);

                  return (
                    <MenuItem
                      key={senderId}
                      style={{ padding: `0 ${config.space.S200}` }}
                      radii="400"
                      onClick={(event) => {
                        openProfile(
                          room.roomId,
                          space?.roomId,
                          senderId,
                          undefined,
                          getMouseEventCords(event.nativeEvent),
                          'Bottom'
                        );
                      }}
                      before={
                        <Avatar shape={AvatarShape.Circle} size="200">
                          <UserAvatar
                            userId={senderId}
                            src={avatarUrl ?? undefined}
                            alt={name}
                            renderFallback={() => userFallbackIcon('sm')}
                          />
                        </Avatar>
                      }
                    >
                      <Box grow="Yes">
                        <Text size="T400" truncate>
                          {name}
                        </Text>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Box>
            </Scroll>
          </Box>
        </Box>
      </Box>
    );
  }
);
